import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { useCallback } from 'react';
import { PartnershipDataEvent, SubscriberDataEvent, useAnalytics } from 'util/analytics';
import { PlaningViewsEnum } from 'components/common/PlanYourTrip/constants';
import { hasKayakIntegration } from 'components/resort/KayakIFrame/kayakLocaleResolver';
import { isSouthernHemisphereCountry } from 'i18n/country';
import { useNoHeader } from 'util/useNoHeader';
import isLiftsUrlWhiteListed from 'util/isLiftsUrlWhiteListed';
import { isSkiRentalsLocale } from 'util/i18nFlags';
import classNames from 'classnames';
import ViewTab from './ViewTab';
import { ContextKey, TRKeysByContextType } from './types';

import styles from './styles.module.scss';

interface ITripControlsBlockProps {
  title: string
  liftsUrl?: string
  rentalUrl?: string
  lessonsUrl?: string
  activeView: PlaningViewsEnum
  onButtonClick: (value: PlaningViewsEnum) => void
  isPartner: boolean,
  contextKey: ContextKey,
  resortCountry?: string,
  hasSkiRental: boolean,
  hasFlights: boolean,
  hasCarRental: boolean,
  redirectSkiRentalPath?: string,
  redirectBookFlightsPath?: string,
  redirectCarRentalPath?: string,
  redirectLodgingPath?: string,
  initialTab: PlaningViewsEnum
}

const TRKeysByContext: TRKeysByContextType<'common'> = {
  resort: 'tripWidget.resortLodging',
  region: 'tripWidget.regionLodging',
};

const TripControlBlock = (
  {
    title,
    liftsUrl,
    activeView,
    rentalUrl: rentalServerUrl,
    redirectSkiRentalPath,
    redirectBookFlightsPath,
    redirectCarRentalPath,
    lessonsUrl,
    onButtonClick,
    isPartner,
    contextKey,
    hasSkiRental,
    hasFlights,
    hasCarRental,
    resortCountry,
    redirectLodgingPath,
    initialTab = PlaningViewsEnum.lodging,
  }: ITripControlsBlockProps,
) => {
  const { t } = useOTSTranslation('common');
  const analytic = useAnalytics();
  const isEUresort = !(isSouthernHemisphereCountry(resortCountry));
  const { noHeader } = useNoHeader();
  const isSkiRentalAPage = hasSkiRental && isSkiRentalsLocale();
  const rentalTabIsAnExternalUrl = !isSkiRentalAPage && isPartner;
  const rentalTabIsAForm = isSkiRentalAPage && initialTab === PlaningViewsEnum.rental;
  const rentalTabIsAnInternalUrl = isSkiRentalAPage && initialTab !== PlaningViewsEnum.rental && !!redirectSkiRentalPath;
  const rentalTabIsAnTab = (isSkiRentalAPage && initialTab !== PlaningViewsEnum.rental && !redirectSkiRentalPath) || (!isSkiRentalAPage && !isPartner);
  const useRentalTabAsUrl = rentalTabIsAnExternalUrl || rentalTabIsAnInternalUrl;
  const useRentalTabAsTab = rentalTabIsAForm || rentalTabIsAnTab;
  const urlToUseForRental = rentalTabIsAnInternalUrl ? redirectSkiRentalPath : rentalServerUrl;
  const showRentalTab = (((!isEUresort && isPartner) || isEUresort) && rentalServerUrl) || isSkiRentalAPage;
  const showLifts = liftsUrl && (isPartner || isLiftsUrlWhiteListed(liftsUrl));
  const withAnalytic = !(noHeader || (!liftsUrl && !lessonsUrl && !showRentalTab && !isPartner));
  const useLodgingAsUrl = activeView === PlaningViewsEnum.flights || activeView === PlaningViewsEnum.carRental;

  const onLodgingClick = useCallback(() => {
    if (!useLodgingAsUrl) {
      onButtonClick(PlaningViewsEnum.lodging);
    }
  }, [isEUresort, useLodgingAsUrl]);

  const onLiftsClick = useCallback(() => {
    analytic.subcriberEvent(SubscriberDataEvent.LiftAccessNavigation, { url: liftsUrl });
  }, [analytic, liftsUrl]);

  const onLessonsClick = useCallback(() => {
    if (isEUresort) {
      onButtonClick(PlaningViewsEnum.lessons);
      return;
    }
    analytic.subcriberEvent(SubscriberDataEvent.LessonsWidget, { url: lessonsUrl });
  }, [analytic, lessonsUrl]);

  const onRentalClick = useCallback(() => {
    if (useRentalTabAsTab) {
      onButtonClick(PlaningViewsEnum.rental);
      return;
    }
    if (rentalTabIsAnExternalUrl && withAnalytic) {
      analytic.subcriberEvent(SubscriberDataEvent.RentalsWidget, { url: rentalServerUrl });
    }
  }, [analytic, rentalServerUrl, rentalTabIsAnExternalUrl, useRentalTabAsTab]);

  const onFlightsClick = useCallback(() => {
    if (withAnalytic) {
      analytic.partnershipEvent(PartnershipDataEvent.FlightsClick);
    }
  }, [analytic, withAnalytic]);

  const onCarRentalClick = useCallback(() => {
    if (withAnalytic) {
      analytic.partnershipEvent(PartnershipDataEvent.CarRentalClick);
    }
  }, [analytic, withAnalytic]);

  const applyActiveStyles = useCallback((key: PlaningViewsEnum) => {
    const activeStyle = key === activeView ? styles.activeTab : styles.viewTab;

    return classNames(activeStyle);
  }, [activeView, initialTab]);

  const isLodgingMode = initialTab === PlaningViewsEnum.lodging;

  const activeLodgingName = (activeView === PlaningViewsEnum.lodging && isLodgingMode)
    ? t(TRKeysByContext[contextKey], { [contextKey]: title })
    : t(TRKeysByContext[contextKey]);

  const showTab = hasKayakIntegration(); // FIXME: get rid of for all locales

  return (
    <div className={styles.tripControlBlock}>
      {!noHeader && (
        <ViewTab
          title={activeLodgingName}
          className={applyActiveStyles(PlaningViewsEnum.lodging)}
          onClick={onLodgingClick}
          url={useLodgingAsUrl ? redirectLodgingPath : undefined}
        />
      )}
      {!noHeader && showRentalTab && (
        <ViewTab
          title={t('tripWidget.skiRental')}
          className={applyActiveStyles(PlaningViewsEnum.rental)}
          onClick={onRentalClick}
          url={useRentalTabAsUrl ? urlToUseForRental : undefined}
        />
      )}
      {!noHeader && showLifts && (
        <ViewTab
          title={t('tripWidget.liftAccess')}
          className={styles.tabLink}
          onClick={onLiftsClick}
          url={liftsUrl}
        />
      )}
      {!noHeader && lessonsUrl && (
        <ViewTab
          title={t('tripWidget.skiLessons')}
          className={applyActiveStyles(PlaningViewsEnum.lessons)}
          onClick={onLessonsClick}
          url={isEUresort ? undefined : lessonsUrl}
        />
      )}
      {showTab && !noHeader && hasCarRental && (
        <ViewTab
          title={t('tripWidget.carRental')}
          className={applyActiveStyles(PlaningViewsEnum.carRental)}
          onClick={onCarRentalClick}
          url={redirectCarRentalPath}
        />
      )}
      {showTab && !noHeader && hasFlights && (
        <ViewTab
          title={t('tripWidget.flights')}
          className={applyActiveStyles(PlaningViewsEnum.flights)}
          onClick={onFlightsClick}
          url={redirectBookFlightsPath}
        />
      )}
    </div>
  );
};

export default TripControlBlock;
